import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';

export interface QuickFilterOption {
  id: string;
  label: ReactNode;
}

export interface QuickFilterGroup {
  id: string;
  label?: ReactNode;
  labelSx?: SxProps<Theme>;
  options: readonly QuickFilterOption[];
  value: readonly string[];
  multiple?: boolean;
  onChange: (value: readonly string[]) => void;
}

export interface QuickFilterBarProps {
  groups: readonly QuickFilterGroup[];
  searchContent?: ReactNode;
  sx?: SxProps<Theme>;
}

export const QuickFilterBar = ({
  groups,
  searchContent,
  sx,
}: QuickFilterBarProps) => {
  const handleOptionChange = (
    group: QuickFilterGroup,
    optionId: string,
  ) => {
    if (group.multiple) {
      const nextValue = group.value.includes(optionId)
        ? group.value.filter((value) => value !== optionId)
        : [...group.value, optionId];

      group.onChange(nextValue);
      return;
    }

    group.onChange(group.value.includes(optionId) ? [] : [optionId]);
  };

  return (
    <Stack
      direction="row"
      spacing={3}
      useFlexGap
      flexWrap="wrap"
      alignItems="center"
      sx={sx}
    >
      {searchContent}
      {searchContent !== undefined && groups.length > 0 && (
        <Divider
          orientation="vertical"
          flexItem
          aria-hidden="true"
          sx={{ mx: 1, display: { xs: 'none', lg: 'block' } }}
        />
      )}
      {groups.map((group, groupIndex) => (
        <Box
          key={group.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          {groupIndex > 0 && (
            <Divider
              orientation="vertical"
              flexItem
              aria-hidden="true"
              sx={{ mx: 1, display: { xs: 'none', lg: 'block' } }}
            />
          )}
          {group.label !== undefined && (
            <Typography
              variant="textSM"
              color="text.secondary"
              sx={group.labelSx}
            >
              {group.label}
            </Typography>
          )}
          {group.options.map((option) => (
            <Chip
              key={option.id}
              label={option.label}
              color={group.value.includes(option.id) ? 'primary' : 'default'}
              onClick={() => handleOptionChange(group, option.id)}
            />
          ))}
        </Box>
      ))}
    </Stack>
  );
};
